import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { TERMS_URL, PRIVACY_URL } from '../constants/legalLinks';

/**
 * TermsPrivacyModal — reusable controlled pop-up for Terms of Service and
 * Privacy Policy links.
 *
 * This is a *controlled* component: it owns no internal open/close state.
 * The caller is responsible for:
 *   1. Maintaining a boolean state value (e.g. `const [open, setOpen] = useState(false)`)
 *   2. Wiring one or more trigger button(s) to call `setOpen(true)`
 *   3. Passing `open` and `onOpenChange` as props below
 *
 * This deliberate split keeps the modal reusable across call sites that need
 * different numbers of trigger buttons (e.g. TopNavBar has one; PatientHeader
 * needs two — one mobile, one desktop — so it cannot share a single
 * Dialog.Trigger inside this component).
 *
 * Dismissal:
 *   - Clicking the × close button
 *   - Pressing Escape (Radix built-in)
 *   - Clicking outside the dialog content / on the backdrop (Radix built-in)
 *
 * Props:
 *   open        {boolean}  — whether the dialog is currently open
 *   onOpenChange {function} — called by Radix with the next open state
 */
export default function TermsPrivacyModal({ open, onOpenChange }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {/*
        Entry + exit: Radix wraps Overlay/Content in Presence. When closing,
        Presence keeps nodes mounted until a *different* animation-name completes
        (see @radix-ui/react-presence). We drive open vs closed animations via
        data-state selectors in App.css (.radix-dialog-* shared classes).
      */}
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />

        {/*
          Content panel: centred card on top of the overlay.
          max-w-sm keeps the card compact since the body is short.
        */}
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-sm w-[calc(100vw-2rem)] z-50 outline-none">

        {/* Close button — top-right corner, accessible label for screen readers */}
        <Dialog.Close asChild>
          <button
            aria-label="Close"
            className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-xl leading-none transition-colors"
          >
            ×
          </button>
        </Dialog.Close>

        <Dialog.Title className="text-lg font-semibold text-gray-900 mb-1 text-left">
          Terms and Privacy
        </Dialog.Title>

        <Dialog.Description asChild>
          {/*
            Using a <div> via asChild rather than the default <p> so we can
            include block-level children (the <ul>) without invalid HTML nesting.
          */}
          <div className="font-medium text-sm text-gray-600 mt-2 text-left">
            <p className="mb-3">
              Here are some links to help you understand how we handle your
              data and protect your privacy:
            </p>
            {/* list-disc renders the standard filled-circle bullet; pl-5 indents the markers into view */}
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-gray-900 underline"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-gray-900 underline"
                >
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>
        </Dialog.Description>

      </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
